import type { CancelablePromise } from "../client"
import { OpenAPI } from "../client"
import {
  listVatWhiteListVerifications,
  searchVatWhiteList,
  type SearchResponse as VATWhiteListSearchResponse,
  type Verification as VATWhiteListVerification,
  verifyVatWhiteListAccount,
} from "../client/contragent-vat"
import { request as __request } from "../client/core/request"

export type ContragentAddressDTO = {
  type: "LEGAL" | "DELIVERY"
  street: string
  building_number: string
  apartment_number?: string
  postal_code: string
  city: string
  country_code: string
  gln?: string
  teryt_code?: string
  lat?: number
  lng?: number
}

export type ContragentBankAccountDTO = {
  iban: string
  swift?: string
  is_whitelisted?: boolean
  last_verification_date?: string
  verification_request_id?: string
  verification_nip?: string
  verification_query_date?: string
  verified_at?: string
}

export type { VATWhiteListSearchResponse, VATWhiteListVerification }

export class VATWhiteListAPIError extends Error {
  code?: string
  retryAfterSeconds?: number

  constructor(message: string, code?: string, retryAfterSeconds?: number) {
    super(message)
    this.name = "VATWhiteListAPIError"
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function normalizeVATWhiteListError(error: any): VATWhiteListAPIError {
  const payload = error?.response?.data ?? error?.error ?? error?.body
  return new VATWhiteListAPIError(
    payload?.error ?? error?.message ?? "Błąd białej listy VAT",
    payload?.code,
    payload?.retry_after_seconds,
  )
}

async function vatWhiteListCall<T>(request: Promise<{ data?: T }>): Promise<T> {
  try {
    const response = await request
    return response.data as T
  } catch (error) {
    throw normalizeVATWhiteListError(error)
  }
}

const vatRequestOptions = () => {
  const configuredBase = String(import.meta.env.VITE_API_DOMAIN ?? "").trim()
  return {
    baseURL:
      configuredBase && configuredBase.toLowerCase() !== "undefined"
        ? configuredBase
        : "/api",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
    },
    throwOnError: true as const,
  }
}

export type ContragentContactDTO = {
  name?: string
  role?: string
  email?: string
  phone?: string
  is_primary?: boolean
}

export type ContragentDeliveryWindowDTO = {
  day: string
  from: string
  to: string
}

export type ContragentPayload = {
  external_id?: string
  type: "ORGANIZATION" | "INDIVIDUAL"
  roles: string[]
  nip?: string
  vat_ue?: string
  regon?: string
  krs?: string
  bdo?: string
  eori?: string
  full_name: string
  short_name?: string
  legal_form?: string
  country_code?: string
  account_manager?: string
  primary_phone?: string
  primary_email?: string
  addresses: ContragentAddressDTO[]
  contacts?: ContragentContactDTO[]
  bank_accounts: ContragentBankAccountDTO[]
  incoterms?: string
  default_currency?: string
  payment_terms_days?: number
  preferred_language?: string
  delivery_windows: ContragentDeliveryWindowDTO[]
}

export type ContragentResponseDTO = ContragentPayload & {
  id: string
  status: "ACTIVE" | "ARCHIVED"
  created_by_id: string
  updated_by_id: string
  archived_by_id?: string
  created_at: string
  updated_at: string
  archived_at?: string
}

export type PostalCodeLookupResponseDTO = {
  postal_code: string
  city: string
  street?: string
  country_code: string
}

export type ContragentImportBatchItemDTO = {
  row_number: number
  akronim: string
  prefiks: string
  nip: string
  kod_p: string
  miasto: string
  ulica: string
  opiekun: string
  telefon: string
  nazwa: string
  email: string
}

export type ContragentImportRowResultDTO = {
  row_number: number
  action: string
  strategy?: string
  contragent_id?: string
  external_id?: string
  full_name?: string
  issues?: string[]
  error?: string
}

export type ContragentImportCSVResponseDTO = {
  dry_run: boolean
  file_name?: string
  total_rows: number
  created_count: number
  updated_count: number
  skipped_count: number
  failed_count: number
  results: ContragentImportRowResultDTO[]
}

export type ContragentLookupFromGUSResponseDTO = {
  company_name: string
  status: string
  start_date: string
  report_name: string
  raw_type: string
  source: string
  provider_mode: string
  mocked: boolean
  mock_note?: string
  request_id?: string
  prefill: {
    type: "ORGANIZATION" | "INDIVIDUAL"
    full_name: string
    short_name?: string
    legal_form?: string
    country_code?: string
    nip?: string
    regon?: string
    krs?: string
    bdo?: string
    addresses: ContragentAddressDTO[]
    roles: string[]
    primary_email?: string
  }
  address: {
    type: "LEGAL" | "DELIVERY"
    street: string
    building_number: string
    apartment_number?: string
    postal_code: string
    city: string
    country_code: string
    voivodeship?: string
    county?: string
    commune?: string
  }
}

export const ContragentService = {
  searchVATWhiteList: async (nip: string, date?: string) => {
    return vatWhiteListCall<VATWhiteListSearchResponse>(
      searchVatWhiteList({
        ...vatRequestOptions(),
        body: { nip, date },
      }),
    )
  },

  verifyVATWhiteList: async (id: string, iban: string, date?: string) => {
    return vatWhiteListCall<VATWhiteListVerification>(
      verifyVatWhiteListAccount({
        ...vatRequestOptions(),
        path: { id },
        body: { iban, date },
      }),
    )
  },

  listVATWhiteListVerifications: async (
    id: string,
    iban?: string,
    limit = 20,
  ) => {
    const response = await vatWhiteListCall<{
      items: VATWhiteListVerification[]
    }>(
      listVatWhiteListVerifications({
        ...vatRequestOptions(),
        path: { id },
        query: { iban, limit },
      }),
    )
    return response.items ?? []
  },
  list: (params?: {
    search?: string
    status?: string
  }): CancelablePromise<ContragentResponseDTO[]> =>
    __request(OpenAPI, {
      method: "GET",
      url: "/v1/contragents",
      query: params,
    }),

  getById: (id: string): CancelablePromise<ContragentResponseDTO> =>
    __request(OpenAPI, {
      method: "GET",
      url: "/v1/contragents/{id}",
      path: { id },
    }),

  lookupPostalCode: (
    postalCode: string,
  ): CancelablePromise<PostalCodeLookupResponseDTO> =>
    __request(OpenAPI, {
      method: "GET",
      url: "/v1/contragents/postal-code/{postalCode}",
      path: { postalCode },
    }),

  lookupByGUS: (
    nip: string,
  ): CancelablePromise<ContragentLookupFromGUSResponseDTO> =>
    __request(OpenAPI, {
      method: "GET",
      url: "/v1/contragents/gus/{nip}",
      path: { nip },
    }),

  importCsv: (
    file: File,
    dryRun = false,
  ): CancelablePromise<ContragentImportCSVResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/contragents/import/csv",
      query: { dry_run: dryRun },
      formData: { file },
      mediaType: "multipart/form-data",
    }),

  batchImport: (
    items: ContragentImportBatchItemDTO[],
    dryRun = false,
  ): CancelablePromise<ContragentImportCSVResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/contragents/batch-import",
      query: { dry_run: dryRun },
      body: { items },
      mediaType: "application/json",
    }),

  create: (
    payload: ContragentPayload,
  ): CancelablePromise<ContragentResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/contragents",
      body: payload,
      mediaType: "application/json",
    }),

  update: (
    id: string,
    payload: ContragentPayload,
  ): CancelablePromise<ContragentResponseDTO> =>
    __request(OpenAPI, {
      method: "PUT",
      url: "/v1/contragents/{id}",
      path: { id },
      body: payload,
      mediaType: "application/json",
    }),

  archive: (id: string): CancelablePromise<ContragentResponseDTO> =>
    __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/contragents/{id}/archive",
      path: { id },
    }),

  restore: (id: string): CancelablePromise<ContragentResponseDTO> =>
    __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/contragents/{id}/restore",
      path: { id },
    }),
}
